// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Normalisation for cross-file comparison (spec §6.3).
 *
 * Three things happen here, and each one exists because of a specific hazard:
 *
 * 1. **Net indices become net names.** Indices are an implementation detail of a
 *    single file (§3.1); comparing them across files is meaningless and
 *    splicing on them is dangerous.
 * 2. **UUIDs and timestamps are dropped.** They are noise once objects are
 *    matched (§6.3), and after a copy/paste they differ on every object.
 * 3. **Zone fill caches are dropped.** They are stale derived data (§3.5) and
 *    would otherwise swamp every real change.
 *
 * Group members are rewritten from raw UUIDs to their objects' stable ids, so a
 * re-pasted group containing the same footprints compares equal.
 */

import {
  atom,
  atoms,
  cloneNode,
  isNode,
  node as mkNode,
  serializeCompact,
  structuralEqual,
  type Node,
} from '../sexp/index.ts';
import type { Board } from '../model/board.ts';

/** Fields dropped from every comparison: identity anchors and timestamps. */
export const NOISE_TOKENS: ReadonlySet<string> = new Set(['uuid', 'tstamp', 'tedit']);

/** Zone fill cache tokens (spec §3.5). */
export const FILL_CACHE_TOKENS: ReadonlySet<string> = new Set([
  'filled_polygon',
  'fill_segments',
  'filled_areas',
]);

export interface NormalizeOptions {
  /** Keep UUIDs in the output. Used by the "show metadata changes" toggle. */
  keepUuids?: boolean;
  /** Keep zone fill caches. Used by the metadata toggle. */
  keepFillCache?: boolean;
  /**
   * Map a group member UUID to a comparison key.
   *
   * The diff supplies a resolver built from the *match* result, so that a group
   * whose members were re-pasted (new UUIDs, same objects) compares equal. The
   * default resolver can only see one file, so it falls back to that file's own
   * stable ids.
   */
  resolveMemberId?: (uuid: string) => string | undefined;
}

/**
 * Produce a comparable clone of a board object.
 *
 * The input node is never modified — the model holds references into the real
 * tree and the merge engine relies on those being pristine.
 */
export function normalizeObject(n: Node, board: Board, options: NormalizeOptions = {}): Node {
  return build(n, board, options);
}

/**
 * Build the normalised tree in a single filtered pass.
 *
 * Deliberately *not* clone-then-strip. On a real board the zone fill caches are
 * the majority of the file — 25 MB of 42 MB on one of the test boards — and
 * cloning them only to drop them made normalisation the dominant cost of a diff.
 * Skipping them during construction never allocates them at all.
 */
function build(n: Node, board: Board, options: NormalizeOptions): Node {
  const out: Node['children'] = [];

  for (const child of n.children) {
    if (!isNode(child)) {
      out.push({ kind: 'atom', value: child.value, quoted: child.quoted });
      continue;
    }
    if (!options.keepUuids && NOISE_TOKENS.has(child.name)) continue;
    if (!options.keepFillCache && FILL_CACHE_TOKENS.has(child.name)) continue;

    if (child.name === 'net') {
      // `(net 12)` and `(net 12 "NAME")` both collapse to `(net "NAME")`.
      const first = atoms(child)[0]?.value;
      const idx = first === undefined ? NaN : Number(first);
      if (Number.isFinite(idx)) {
        const name = board.netNameByIndex.get(idx);
        out.push(mkNode('net', [atom(name ?? `<unknown net index ${idx}>`, true)]));
        continue;
      }
    }

    if (child.name === 'members') {
      out.push(normalizeMembers(child, board, options));
      continue;
    }

    out.push(build(child, board, options));
  }

  return { kind: 'node', name: n.name, children: out };
}

/**
 * Group membership by stable object identity rather than raw UUID, and sorted —
 * a group is a set, and copy/paste renumbers every UUID in it (§3.2, §3.6).
 */
function normalizeMembers(members: Node, board: Board, options: NormalizeOptions): Node {
  const ids = atoms(members).map((a) => {
    const resolved = options.resolveMemberId?.(a.value);
    if (resolved !== undefined) return resolved;
    const obj = board.byUuid.get(a.value);
    return obj ? obj.id : `uuid:${a.value}`;
  });
  ids.sort();
  return mkNode('members', ids.map((id) => atom(id, true)));
}

/**
 * Normalise a *global* block for V4–V6 (spec §6.5).
 *
 * "Parse, sort children by token name where order is not semantically
 * significant, drop UUID-only fields, re-serialize, compare strings. Be
 * conservative — if unsure whether order matters for a given block, treat it as
 * significant."
 *
 * Only the *direct* children of the listed blocks are sorted. Nothing deeper is
 * reordered, because deeper structures (the stackup layer list, plot parameter
 * order) may well be positional.
 */
export const SORTABLE_BLOCKS: ReadonlySet<string> = new Set(['setup', 'general', 'properties']);

export function normalizeGlobalBlock(n: Node): Node {
  const clone = cloneNode(n);
  stripNoise(clone);
  if (SORTABLE_BLOCKS.has(clone.name)) sortDirectChildren(clone);
  return clone;
}

function stripNoise(n: Node): void {
  const out: Node['children'] = [];
  for (const child of n.children) {
    if (isNode(child)) {
      if (NOISE_TOKENS.has(child.name)) continue;
      stripNoise(child);
    }
    out.push(child);
  }
  n.children = out;
}

function sortDirectChildren(n: Node): void {
  const nodes = n.children.filter(isNode);
  const rest = n.children.filter((c) => !isNode(c));
  nodes.sort((a, b) => {
    if (a.name !== b.name) return a.name < b.name ? -1 : 1;
    const sa = serializeCompact(a);
    const sb = serializeCompact(b);
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  });
  n.children = [...rest, ...nodes];
}

/** True when two normalised trees are identical. */
export function sameTree(a: Node, b: Node): boolean {
  return structuralEqual(a, b);
}
